// playerStore.js
import { create } from 'zustand';
import useQueueStore from './queueStore';
import { useRoleStore } from './roleStore';
import { fetchNextPlayable } from '../services/partyStarters';
import useEventStore from './eventStore';
import { isEventHost } from '../utils/identity';
import { parseDurationSeconds, recoverHostPosition, shouldAdvanceOnEnded } from '../utils/playbackClock';
import { requestHostSeek } from '../utils/hostSeek';

let playerPublisher = null;

export function setPlayerPublisher(fn) {
  playerPublisher = typeof fn === 'function' ? fn : null;
}

function canMutateSharedPlayback() {
  const { userId, hostId } = useRoleStore.getState();
  return isEventHost(userId, hostId);
}

function publishPlayer(reason, extras) {
  if (!playerPublisher) return;
  playerPublisher(reason, extras);
}

function trackPlayedSong(state, song) {
  if (!song) return state;

  const playedSongs = [...state.playedSongs, song.id].slice(-50);
  const playedSignatures = new Set(state.playedSignatures);
  const playedArtists = new Map(state.playedArtists);

  const signature = `${song.title}-${song.artist}`.toLowerCase()
    .replace(/[()[\]\-–_:|]/g, '')
    .replace(/\b(hd|lyrics|letra|traducida|audio|video|cover|remix|version)\b/g, '')
    .trim();
  playedSignatures.add(signature);

  const artist = (song.artist || '').toLowerCase();
  playedArtists.set(artist, (playedArtists.get(artist) || 0) + 1);
  if (playedArtists.size > 50) {
    const entries = Array.from(playedArtists.entries()).slice(-50);
    return {
      ...state,
      playedSongs,
      playedSignatures,
      playedArtists: new Map(entries),
    };
  }

  return { ...state, playedSongs, playedSignatures, playedArtists };
}

const usePlayerStore = create((set, get) => ({
  isPlaying: false,
  currentSong: null,
  volume: 100,
  currentTime: 0,
  duration: 0,
  playerInstance: null,
  isLoadingNext: false,
  remotePositionSeconds: 0,
  remoteUpdatedAtMs: 0,
  applyGeneration: 0,
  playerRecoveryStatus: 'idle',

  playedSongs: [],
  playedSignatures: new Set(),
  playedArtists: new Map(),

  applyRemotePlayer: ({ song, isPlaying, positionSeconds, updatedAtMs } = {}) => {
    const position = Math.max(0, Number(positionSeconds) || 0);
    set((state) => ({
      currentSong: song ?? null,
      isPlaying: Boolean(isPlaying),
      currentTime: position,
      remotePositionSeconds: position,
      remoteUpdatedAtMs: Number(updatedAtMs) || Date.now(),
      applyGeneration: state.applyGeneration + 1,
    }));
  },

  setPlayerRecoveryStatus: (status) => set({ playerRecoveryStatus: status }),

  /**
   * Re-acquire a party as host from the sync/player checkpoint.
   *
   * Recovery always lands paused. A stored `isPlaying: true` is not proof that
   * audio is running: nothing publishes a final pause when a host tab closes,
   * so that flag routinely outlives the tab that wrote it. Adopting it left the
   * transport claiming playback that no live player instance had confirmed,
   * which no amount of pressing Play could clear. The stored flag now only
   * decides whether a still-fresh checkpoint's position may be advanced.
   */
  applyHostRecovery: ({ song, isPlaying, positionSeconds, updatedAtMs } = {}) => {
    const recoveredPosition = recoverHostPosition(
      positionSeconds,
      Boolean(isPlaying),
      updatedAtMs,
      Date.now(),
      parseDurationSeconds(song?.duration),
    );
    set((state) => ({
      ...trackPlayedSong(state, song),
      currentSong: song,
      isPlaying: false,
      currentTime: recoveredPosition,
      remotePositionSeconds: recoveredPosition,
      remoteUpdatedAtMs: Date.now(),
      playerRecoveryStatus: 'recovered',
      applyGeneration: state.applyGeneration + 1,
    }));
  },

  setIsPlaying: (isPlaying, options = {}) => {
    if (options.remote) {
      set({ isPlaying: Boolean(isPlaying) });
      return;
    }
    if (!canMutateSharedPlayback()) return;
    set({ isPlaying: Boolean(isPlaying) });
    publishPlayer(isPlaying ? 'play' : 'pause', { isPlaying: Boolean(isPlaying) });
  },

  setCurrentSong: (song, options = {}) => {
    if (options.remote) {
      set((state) => ({
        ...trackPlayedSong(state, song),
        currentSong: song,
        isPlaying: song ? true : state.isPlaying,
        currentTime: 0,
      }));
      return true;
    }
    if (!canMutateSharedPlayback()) return false;

    const reason = options.reason || 'song';
    set((state) => ({
      ...trackPlayedSong(state, song),
      currentSong: song,
      isPlaying: song ? true : state.isPlaying,
      currentTime: 0,
    }));
    publishPlayer(reason, {
      song,
      isPlaying: Boolean(song),
      positionSeconds: 0,
    });
    return true;
  },

  setVolume: (volume) => set({ volume }),
  setCurrentTime: (time) => set({ currentTime: time }),
  setDuration: (duration) => set({ duration }),
  setPlayerInstance: (player) => set({ playerInstance: player }),

  playPause: () => {
    if (!canMutateSharedPlayback()) return;
    const player = get().playerInstance;
    const isPlaying = get().isPlaying;
    if (player) {
      try {
        if (isPlaying) player.pauseVideo();
        else player.playVideo();
      } catch (error) {
        console.error('Error in playPause:', error);
      }
      return;
    }
    get().setIsPlaying(!isPlaying);
  },

  seekTo: (seconds) => {
    if (!canMutateSharedPlayback()) return false;
    const position = Math.max(0, Number(seconds) || 0);
    const player = get().playerInstance;
    const wantPlaying = get().isPlaying;
    try {
      player?.seekTo?.(position, true);
      if (!wantPlaying) {
        player?.pauseVideo?.();
      }
    } catch (error) {
      console.error('Error seeking:', error);
    }
    set({ currentTime: position });
    requestHostSeek({ targetSeconds: position, wantPlaying });
    return true;
  },

  notifyPlaybackEnded: () => {
    if (!canMutateSharedPlayback()) return;
    set({ isPlaying: false });
    publishPlayer('ended', { isPlaying: false });
  },

  handleEnded: async () => {
    if (!shouldAdvanceOnEnded(canMutateSharedPlayback())) return false;
    const advanced = await get().skipSong();
    if (!advanced) {
      get().notifyPlaybackEnded();
    }
    return advanced;
  },

  calculateTitleSimilarity: (str1, str2) => {
    const a = str1.split(' ');
    const b = str2.split(' ');
    const intersection = a.filter(word => b.includes(word));
    const union = new Set([...a, ...b]);
    return intersection.length / union.size;
  },

  skipSong: async (options = {}) => {
    if (!canMutateSharedPlayback()) return false;

    const reason = options.reason || 'skip';
    const allowAutofill = reason !== 'playback_error';
    const { currentSong, setCurrentSong, isLoadingNext } = get();
    const { queue, addToQueue } = useQueueStore.getState();

    if (isLoadingNext) return false;

    if (currentSong && queue.length > 0) {
      const currentIndex = queue.findIndex((s) => s.id === currentSong.id);
      const nextSong = currentIndex >= 0 ? queue[currentIndex + 1] : null;
      if (reason === 'playback_error' && currentIndex >= 0) {
        const removal = useQueueStore.getState().removePlaybackFailure(currentSong.id);
        if (removal?.removed && removal.next) {
          setCurrentSong(removal.next, { reason: 'skip' });
          return true;
        }
        if (removal?.removed) {
          setCurrentSong(null, { reason: 'playback_error' });
          set({ isPlaying: false });
        }
      }
      if (nextSong) {
        setCurrentSong(nextSong, { reason: 'skip' });
        return true;
      }
    }

    if (!allowAutofill && reason !== 'playback_error') return false;
    if (!currentSong?.id && reason !== 'playback_error') return false;

    set({ isLoadingNext: true });

    try {
      const eventId = useEventStore.getState().eventData?.id || null;
      const excludeRecordingIds = [];
      if (currentSong?.recordingId) excludeRecordingIds.push(currentSong.recordingId);
      queue.forEach((item) => {
        if (item?.recordingId) excludeRecordingIds.push(item.recordingId);
      });
      const next = eventId
        ? await fetchNextPlayable(eventId, { excludeRecordingIds })
        : null;
      if (!next?.id || !next.providerAssetKey || next.id === currentSong?.id) {
        return false;
      }
      const recommended = {
        ...next,
        addedAt: new Date().toISOString(),
        addedBy: 'system',
        userRole: 'host',
      };
      addToQueue(recommended);
      setCurrentSong(recommended, { reason: 'skip' });
      set({ isPlaying: true });
      return true;
    } catch (err) {
      console.error('Error fetching recommendation:', err);
      return false;
    } finally {
      set({ isLoadingNext: false });
    }
  },

  previousSong: () => {
    if (!canMutateSharedPlayback()) return false;
    const { currentSong } = get();
    const queue = useQueueStore.getState().queue;
    if (currentSong && queue.length > 0) {
      const currentIndex = queue.findIndex((song) => song.id === currentSong.id);
      if (currentIndex > 0) {
        get().setCurrentSong(queue[currentIndex - 1], { reason: 'previous' });
        return true;
      }
    }
    return false;
  },

  resetPlayer: () =>
    set({
      isPlaying: false,
      currentSong: null,
      volume: 100,
      currentTime: 0,
      duration: 0,
      remotePositionSeconds: 0,
      remoteUpdatedAtMs: 0,
      applyGeneration: 0,
      playerRecoveryStatus: 'idle',
      playedSongs: [],
      playedSignatures: new Set(),
      playedArtists: new Map(),
    }),
}));

export default usePlayerStore;
